import React, { useEffect, useState } from 'react';
import Modal from 'components/ui/Modal';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';
import { INSTALACIONES } from 'lib/auditoriaInstalaciones';
import { prepararArbol, arbolAFormulario } from 'lib/arboles';
import { Campo, Texto } from './Campos';
import { mensajeError } from 'lib/errores';

// Alta y edición de la ficha de un árbol. onGuardar(datos) devuelve una
// promesa; si lanza, el error se muestra aquí y el formulario queda abierto.
export default function FormArbol({ open, onClose, arbol, instalacion = 'canopy', onGuardar }) {
  const [form, setForm] = useState(() => arbolAFormulario(arbol || { instalacion }));
  const [errores, setErrores] = useState({});
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(arbolAFormulario(arbol || { instalacion }));
    setErrores({});
    setError('');
    setEnviando(false);
  }, [open, arbol, instalacion]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (enviando) return;
    const { datos, errores: e } = prepararArbol(form);
    setErrores(e);
    if (Object.keys(e).length) return;
    setEnviando(true);
    setError('');
    try {
      await onGuardar(datos);
      onClose();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo guardar.'));
      setEnviando(false);
    }
  };

  return (
    <Modal open={open} onClose={enviando ? () => {} : onClose} title={arbol ? `Editar árbol ${arbol.codigo}` : 'Registrar árbol'}
      maxWidth="max-w-lg" cerrarConFondo={false}
      footer={(
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="lg" className="flex-1 px-4" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button type="button" size="lg" className="flex-1 px-4" onClick={guardar} loading={enviando}>
            {arbol ? 'Guardar cambios' : 'Registrar'}
          </Button>
        </div>
      )}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo label="Código" requerido error={errores.codigo} ayuda="Por ejemplo A1, A2…">
          <Texto value={form.codigo} onChange={set('codigo')} data-autofocus autoCapitalize="characters" />
        </Campo>
        <Campo label="Instalación">
          <Select value={form.instalacion} onChange={set('instalacion')} aria-label="Instalación" className="w-full">
            {INSTALACIONES.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </Select>
        </Campo>
        <Campo label="Especie (nombre científico)">
          <Texto value={form.especie} onChange={set('especie')} />
        </Campo>
        <Campo label="Nombre común">
          <Texto value={form.nombre_comun} onChange={set('nombre_comun')} />
        </Campo>
        <Campo label="Qué sostiene" ayuda="Por ejemplo: Plataforma 4">
          <Texto value={form.sostiene} onChange={set('sostiene')} />
        </Campo>
        <Campo label="Ubicación">
          <Texto value={form.ubicacion} onChange={set('ubicacion')} />
        </Campo>
        <Campo label="DAP (cm)" error={errores.dap_cm} ayuda="Diámetro a la altura del pecho">
          <Texto value={form.dap_cm} onChange={set('dap_cm')} inputMode="decimal" />
        </Campo>
        <Campo label="Altura (m)" error={errores.altura_m}>
          <Texto value={form.altura_m} onChange={set('altura_m')} inputMode="decimal" />
        </Campo>
        <Campo label="Latitud" error={errores.lat}>
          <Texto value={form.lat} onChange={set('lat')} inputMode="decimal" placeholder="9.6512" />
        </Campo>
        <Campo label="Longitud" error={errores.lng}>
          <Texto value={form.lng} onChange={set('lng')} inputMode="decimal" placeholder="-84.6321" />
        </Campo>
        <Campo label="Notas" className="sm:col-span-2">
          <Texto area rows={3} value={form.notas} onChange={set('notas')} />
        </Campo>
      </div>
      {arbol && <p className="mt-3 text-xs text-muted-foreground">Cada cambio queda en el historial de la ficha con quién y cuándo.</p>}
      {error && <p role="alert" className="mt-3 rounded-lg bg-error/10 px-2.5 py-2 text-sm text-error">{error}</p>}
    </Modal>
  );
}
